import React, { useEffect, useMemo, useState } from "react"
import {
  type LDContext,
  LDProvider,
  useLDClient,
} from "launchdarkly-react-client-sdk"
import config from "@/config"
import { useUser } from "@/queries/user"
import { getSegmentAnonUserId } from "@/analytics/client"
import { $isLaunchDarklyInitialized } from "@/stores/launchDarkly"

const ldOptions = { streaming: true }
const ldReactOptions = { useCamelCaseFlagKeys: false }
const ldTimeout = 5

const LDContextUpdater = () => {
  const { data: user, isLoading } = useUser()
  const [key, setKey] = useState<string | null>(null)
  const ldClient = useLDClient()

  useEffect(() => {
    if (ldClient && !isLoading) {
      void (async () => {
        try {
          const anonymousId = await getSegmentAnonUserId()

          const newKey =
            anonymousId +
            user?.me?.email +
            user?.me?.firstName +
            user?.me?.lastName

          if (key === newKey) {
            console.log("User already identified in LaunchDarkly")
            return
          }

          const ldContext: LDContext = {
            kind: "user",
            key: anonymousId,
            name: user?.me
              ? `${user.me.firstName} ${user.me.lastName}`
              : undefined,
            email: user?.me?.email,
            anonymous: true,
          }

          console.log(
            "LDContextUpdater: Identifying user in LaunchDarkly",
            ldContext
          )

          await ldClient.identify(ldContext)
          setKey(newKey)
        } catch (e) {
          console.error("Failed to identify", e, ldClient.getContext())
        } finally {
          $isLaunchDarklyInitialized.set(true)
        }
      })()
    }
  }, [ldClient, user, isLoading, key])

  return null
}

export const LDProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return useMemo(() => {
    return (
      <LDProvider
        clientSideID={config.LAUNCHDARKLY_CLIENT_SIDE_ID}
        options={ldOptions}
        reactOptions={ldReactOptions}
        timeout={ldTimeout}
      >
        <LDContextUpdater />
        {children}
      </LDProvider>
    )
  }, [])
}
